// Runtime app configuration (public.app_config, single row).
//
// WHY RUNTIME AND NOT A BUILD CONSTANT
//   billing_mode has to be flippable without shipping an APK. Once a build
//   is on the Play Store there is no other way to change how it behaves,
//   and the whole point of web_info mode is that it gets switched off the
//   day real in-app purchase goes live.
//
// The table is read-only to `authenticated` (see
// supabase/billing_web_info_migration.sql) — a client that could write
// billing_mode='play' would re-expose the purchase UI to itself.

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useOnAppResume } from "@/hooks/useOnAppResume";
import { supabase } from "@/lib/supabase";
import {
  DEFAULT_BILLING_MODE,
  type BillingMode,
} from "@/lib/featureFlags";

export interface AppConfig {
  billing_mode: BillingMode;
  waba_enabled: boolean;
}

const FALLBACK: AppConfig = {
  billing_mode: DEFAULT_BILLING_MODE,
  waba_enabled: false,
};

/** Config rarely changes, and a stale read is harmless — it only decides
 *  which paywall to draw. Long window keeps this off the hot path; a flip
 *  still lands promptly because useRefreshAppConfigOnResume marks the
 *  entry stale every time the app returns to the foreground. */
const CONFIG_STALE_MS = 15 * 60_000; // 15 min

export const appConfigQueryKey = ["appConfig"] as const;

/**
 * Read the single app_config row.
 *
 * Never throws and never returns null: any failure — offline, RLS change,
 * table missing because the migration hasn't run — resolves to the
 * hardcoded fallback. A paywall that fails to render because config
 * didn't load would be worse than one that renders in its safe mode.
 */
export async function fetchAppConfig(): Promise<AppConfig> {
  try {
    const { data, error } = await supabase
      .from("app_config")
      .select("billing_mode, waba_enabled")
      .eq("id", 1)
      .maybeSingle();
    if (error || !data) {
      if (error) {
        // eslint-disable-next-line no-console
        console.warn("[appConfig] fetch failed, using fallback", error);
      }
      return FALLBACK;
    }
    const row = data as { billing_mode?: string; waba_enabled?: boolean };
    // Validate rather than trust: an unrecognised mode (someone typo'd it
    // in the dashboard) must not put the app in an undefined state.
    const mode: BillingMode =
      row.billing_mode === "play" || row.billing_mode === "web_info"
        ? row.billing_mode
        : DEFAULT_BILLING_MODE;
    return { billing_mode: mode, waba_enabled: row.waba_enabled === true };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("[appConfig] fetch threw, using fallback", err);
    return FALLBACK;
  }
}

/** Full config, with the fallback as placeholder data so consumers never
 *  see undefined and never need a loading branch. */
export function useAppConfig() {
  const query = useQuery<AppConfig, Error>({
    queryKey: appConfigQueryKey,
    queryFn: fetchAppConfig,
    staleTime: CONFIG_STALE_MS,
    // The app-wide default is refetchOnMount:false, which would ignore the
    // stale mark from a resume and keep drawing the old paywall until a
    // cold start. true = refetch on mount only when stale.
    refetchOnMount: true,
    placeholderData: FALLBACK,
  });
  return {
    config: query.data ?? FALLBACK,
    isLoading: query.isPending,
  };
}

/**
 * Just the billing mode — the common case.
 *
 *   const { mode, isWebInfo } = useBillingMode();
 */
export function useBillingMode() {
  const { config, isLoading } = useAppConfig();
  return {
    mode: config.billing_mode,
    isWebInfo: config.billing_mode === "web_info",
    isPlay: config.billing_mode === "play",
    isLoading,
  };
}

/**
 * Re-read app_config whenever the app comes back to the foreground.
 *
 * This is what makes a billing_mode flip reach an already-running install:
 * launch fetches fresh, and every resume invalidates the cached row. A
 * mounted paywall refetches immediately; an unmounted one refetches the
 * next time it mounts. Mount once, globally (AppShell).
 */
export function useRefreshAppConfigOnResume() {
  const queryClient = useQueryClient();
  useOnAppResume(() => {
    void queryClient.invalidateQueries({ queryKey: appConfigQueryKey });
  });
}
